// Vitality Awakening App
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { Navigation } from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useFavicon } from "@/hooks/useFavicon";

// Lazy load pages for code splitting
const Home = lazy(() => import("./pages/Home"));
const Journeys = lazy(() => import("./pages/Journeys"));
const JourneyDetail = lazy(() => import("./pages/JourneyDetail"));
const Sessions = lazy(() => import("./pages/Sessions"));
const Coaching = lazy(() => import("./pages/Coaching"));
const Community = lazy(() => import("./pages/Community"));
const Members = lazy(() => import("./pages/Members"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FAQ = lazy(() => import("./pages/FAQ"));
const LandingPage = lazy(() => import("./pages/LandingPage"));

const queryClient = new QueryClient();

const FaviconLoader = () => {
  useFavicon();
  return null;
};

// Known routes that are NOT landing pages
const knownRoutes = [
  '/',
  '/journeys',
  '/sessions',
  '/coaching',
  '/community',
  '/members',
  '/dashboard',
  '/privacy-policy',
  '/terms-of-service',
  '/faq',
  '/auth'
];

const AppContent = () => {
  const location = useLocation();
  
  // Check if current path is a landing page (not a known route and has a slug pattern)
  const isLandingPage = !knownRoutes.includes(location.pathname) && 
    !location.pathname.startsWith('/journeys/') &&
    location.pathname !== '/' &&
    location.pathname.split('/').length === 2 &&
    location.pathname.split('/')[1] !== '';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {!isLandingPage && <Navigation />}
      <main className={isLandingPage ? "flex-1" : "container mx-auto px-4 py-8 flex-1"}>
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/journeys" element={<Journeys />} />
            <Route path="/journeys/:id" element={<JourneyDetail />} />
            <Route path="/sessions" element={<Sessions />} />
            <Route path="/coaching" element={<Coaching />} />
            <Route path="/community" element={<Community />} />
            <Route path="/members" element={
              <ProtectedRoute>
                <Members />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/:slug" element={<LandingPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isLandingPage && <Footer />}
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <FaviconLoader />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
